import { FunctionComponent } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import { Typography } from '../Typography/Typography';

import { IconButton, IconButtonProps } from '../IconButton/IconButton';
import { ReportDialog, ReportDialogProps } from '../ReportDialog/ReportDialog';
import { usePlayer } from '../../hooks/PlayerProvider';
import { useReportContext } from '../../hooks/useReportContext';
import { useTracker } from '../../hooks/useTracker';
import { useApis } from '../../hooks/ApisProvider';
import { useProvider } from '../../hooks/useProvider';
import { useConfig } from '../../hooks/ConfigProvider';
import { usePlayerFullscreen } from '../../hooks/usePlayerFullscreen';
import { useIcons } from '../../hooks/useIcons';
import { useTranslation } from '../../../../utils/useTranslation';
import ns from '../../translations';

export const ReportButton: FunctionComponent<
  IconButtonProps & { fontSize?: 'large' | 'medium' }
> = (props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const player = usePlayer();
  const { configProvider } = useProvider();
  const { vgtvApi } = useApis();
  const { trackButtonClick } = useTracker();
  const { getDefaultContext } = useReportContext();
  const { getVariant } = useConfig();
  const { exitFullscreen } = usePlayerFullscreen();
  const { Message } = useIcons();
  const { t } = useTranslation(ns);

  const { className, fontSize, ...rest } = props;

  const canReportIssue =
    getVariant('error-reporting') === 'enabled' && configProvider === 'vgtv';

  const handleSubmit: ReportDialogProps['onSubmit'] = useCallback(
    async (values) => {
      setIsDialogOpen(false);

      const context = await getDefaultContext.current();

      await vgtvApi.createFeedback({
        ...values,
        event: {
          message: `[user feedback] ${values.comments?.replace(/\s+/gm, ' ')}`,
          captureContext: context,
        },
      });
    },
    [getDefaultContext, vgtvApi]
  );

  const handleClick = useCallback(() => {
    player.pause();
    setIsDialogOpen(true);
    exitFullscreen();

    trackButtonClick('report');
  }, [trackButtonClick, player, exitFullscreen]);

  const handleClose = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  return (
    <div onClick={handleClick} className={className}>
      <Typography variant="label" size={fontSize} color="primary">
        {t('Feedback')}
      </Typography>
      <IconButton {...rest} color="transparent-primary">
        <Message />
      </IconButton>
      <ReportDialog
        variant="feedback"
        isOpen={isDialogOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </div>
  );
};
